'use client'
import 'leaflet/dist/leaflet.css';
import L from "leaflet";
import { useEffect, useState, useRef } from 'react';
import "leaflet-curve";
import * as polyline from '@mapbox/polyline';
import { getDistance, getPolylineStringFromCoordinates } from './helper';
interface TripAddress {
  markerType: string;
  title: string;
  address: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

interface WaypointStatus {
  tripStatus: string;
}

interface DriverLocation {
  latitude: number;
  longitude: number;
}

interface RideTrackingOSMapProps {
  tripAddress?: TripAddress[];
  wayPointsTripStatus?: WaypointStatus[];
  driverLocation?: DriverLocation | null;
  driverRouteString?: string;
  showRoute?: boolean;
  height?: string;
  // setDuration?: (duration: string | null) => void;
}

export default function RideTrackingOSMap({
  tripAddress = [],
  wayPointsTripStatus = [],
  driverLocation = null,
  driverRouteString = '',
  showRoute = true,
  height = '100%',
  // setDuration = () => {}
}: RideTrackingOSMapProps) {

  const [mapState, setMap] = useState<any>(null);
  const mapIdRef = useRef(`map-${Math.random().toString(36).substr(2, 9)}`);
  const markersRef = useRef<any[]>([]);
  const polylineRef = useRef<any>(null);
  const driverMarkerRef = useRef<any>(null);
  const [routeCoordinates, setRouteCoordinates] = useState<Array<{ latitude: number; longitude: number }>>([]);
  const [routeDuration, setRouteDuration] = useState<number | null>(null);
  const [routeDurationText, setRouteDurationText] = useState<string | null>(null);
  const hasUserInteractedRef = useRef(false);
  const isInitialFitRef = useRef(true);
  const interactionTimeoutRef = useRef<number | null>(null);
  const lastBoundsRef = useRef<any>(null);
  const animationFrameRef = useRef<number | null>(null);
  const previousDriverLocationRef = useRef<DriverLocation | null>(null);
  const currentBearingRef = useRef<number>(0); // Store current bearing/rotation
  const routeAnimationTokenRef = useRef<number>(0); // increment to cancel in-flight route-follow sequences
  const lastRerouteRef = useRef<number>(0);
  const MIN_REROUTE_INTERVAL_MS = 30000; // throttle reroute calls
  const DEVIATION_THRESHOLD_M = 50; // off-route threshold in meters


  const isValidCoordinate = (lat: number, lng: number): boolean => {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  };

  const decodePolylineString = (polylineString: string): Array<{ latitude: number; longitude: number }> => {

    if (!polylineString) return [];

    // Try precision 5 first (Google Maps standard / OSRM default)
    try {
      const decoded5 = polyline.decode(polylineString);
      if (decoded5 && decoded5.length > 0) {
        const coords = decoded5.map((point: [number, number]) => ({
          latitude: point[0],
          longitude: point[1]
        }));
        // Validate that at least some coordinates are in a real location (not clustered at 0,0)
        const validCount = coords.filter(c => isValidCoordinate(c.latitude, c.longitude) && (Math.abs(c.latitude) > 0.1 || Math.abs(c.longitude) > 0.1)).length;
        if (validCount > 0) return coords;
      }
    } catch (error5) {
      // precision 5 failed, try precision 6
    }

    // Fallback: try precision 6
    try {
      const decoded6 = polyline.decode(polylineString, 6);
      if (decoded6 && decoded6.length > 0) {
        return decoded6.map((point: [number, number]) => ({
          latitude: point[0],
          longitude: point[1]
        }));
      }
    } catch (error6) {
    }

    return [];
  };



  const isDriverOffRoute = (driverLocation: { latitude: number; longitude: number }, route: Array<{ latitude: number; longitude: number }>, threshold: number) => {
    if (!driverLocation || !route || route.length === 0) return false;
    let minDist = Number.POSITIVE_INFINITY;
    for (let i = 0; i < route.length; i++) {
      const d = getDistance(driverLocation?.latitude, driverLocation?.longitude, route[i]?.latitude, route[i]?.longitude);
      if (d < minDist) minDist = d;
    }
    return minDist > threshold;
  };

  useEffect(() => {

    try {
      // if (!driverLocation || !driverLocation.latitude || !driverLocation.longitude) return;
      if (!tripAddress || tripAddress.length === 0) return;
      const dest = tripAddress[tripAddress.length - 1]?.location;
      if (!dest || !dest.latitude || !dest.longitude) return;
      if (wayPointsTripStatus && wayPointsTripStatus.length > 0) {
        tripAddress = tripAddress.slice(wayPointsTripStatus.length);
      }

      const validCoords = tripAddress
        .filter((addr) => addr?.location?.latitude && addr?.location?.longitude)
        .map((addr) => ({
          latitude: addr.location.latitude,
          longitude: addr.location.longitude,
        }));
      (async () => {
        const polyRes = driverRouteString;


        if (polyRes) {
          const decoded = decodePolylineString(polyRes);

          if (decoded && decoded.length >= 2) {
            setRouteCoordinates(decoded);
            lastRerouteRef.current = Date.now();
            // if (polyRes.duration) {
            //   const durationNum = Number(polyRes?.duration);
            //   const minutes = Math.floor(durationNum / 60);
            //   const seconds = Math.floor(durationNum % 60);
            //   setDuration(`${minutes} min ${seconds} sec`);
            // } else {
            //   setRouteDuration(null);
            //   setRouteDurationText(null);
            // }
          }
        }
      })();
    } catch (e) {
      // ignore
    }
  }, [driverRouteString, tripAddress, wayPointsTripStatus, driverLocation]);

  // Calculate bearing between two coordinates (returns degrees from north)
  const calculateBearing = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const toRadians = (deg: number) => deg * Math.PI / 180;
    const toDegrees = (rad: number) => rad * 180 / Math.PI;

    const dLng = toRadians(lng2 - lng1);
    const lat1Rad = toRadians(lat1);
    const lat2Rad = toRadians(lat2);

    const y = Math.sin(dLng) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
      Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);

    const bearing = toDegrees(Math.atan2(y, x));
    return (bearing + 360) % 360; // Normalize to 0-360
  };

  // Store last valid route index to prevent backward jumps
  const lastRouteIndexRef = useRef<number>(0);

  // Check if new location is a forward movement along the route
  const isForwardMovement = (
    prevLat: number,
    prevLng: number,
    newLat: number,
    newLng: number
  ): boolean => {
    // If no route, assume forward movement
    if (routeCoordinates.length === 0) return true;

    // Calculate actual distance between old and new location
    const actualDistanceMeters = haversineDistanceMeters(prevLat, prevLng, newLat, newLng);

    // If the movement is very small (< 10 meters), it's likely GPS jitter - ignore it
    if (actualDistanceMeters < 10) {
      console.log('Ignoring GPS jitter - movement too small:', actualDistanceMeters.toFixed(2), 'm');
      return false;
    }

    // Find closest point on route to new location (search from last known index for efficiency)
    const searchStart = Math.max(0, lastRouteIndexRef.current - 20);
    const searchEnd = Math.min(routeCoordinates.length - 1, lastRouteIndexRef.current + 100);

    let minDistNew = Infinity;
    let newRouteIndex = lastRouteIndexRef.current;

    for (let i = searchStart; i <= searchEnd; i++) {
      const coord = routeCoordinates[i];
      if (!coord) continue;
      const dist = Math.sqrt(
        Math.pow(coord.latitude - newLat, 2) + Math.pow(coord.longitude - newLng, 2)
      );
      if (dist < minDistNew) {
        minDistNew = dist;
        newRouteIndex = i;
      }
    }

    // Check if we're moving forward along the route
    const indexDiff = newRouteIndex - lastRouteIndexRef.current;

    // Allow forward movement and small backward movements (GPS jitter tolerance)
    if (indexDiff >= -3) {
      // Update last known index
      if (indexDiff >= 0) {
        lastRouteIndexRef.current = newRouteIndex;
      }
      return true;
    }

    // For larger backward movements, check actual distance
    // If actual distance is reasonable (< 100m), it's likely GPS inaccuracy, allow it
    if (actualDistanceMeters < 100) {
      console.log('Allowing backward movement - small distance:', actualDistanceMeters.toFixed(2), 'm');
      return true;
    }

    // Reject significant backward movements
    console.log('Rejecting backward movement - large distance:', actualDistanceMeters.toFixed(2), 'm, index diff:', indexDiff);
    return false;
  };

  // Find closest index on decoded route to a given lat/lng
  const findClosestRouteIndex = (lat: number, lng: number): number => {
    if (routeCoordinates.length === 0) return -1;
    let minDist = Infinity;
    let bestIndex = -1;
    routeCoordinates.forEach((coord, idx) => {
      const d = Math.pow(coord.latitude - lat, 2) + Math.pow(coord.longitude - lng, 2);
      if (d < minDist) {
        minDist = d;
        bestIndex = idx;
      }
    });
    return bestIndex;
  };

  // Haversine distance (meters)
  const haversineDistanceMeters = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const toRad = (d: number) => d * Math.PI / 180;
    const R = 6371000; // meters
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Smooth animation function for driver marker
  // Animate marker between two points. Returns a Promise that resolves when animation completes.
  const animateDriverMarker = async (
    startLat: number,
    startLng: number,
    endLat: number,
    endLng: number,
    bearing: number,
    duration: number = 1000, // 1 second animation by default
    animationToken: number // Token to check if animation should continue
  ): Promise<void> => {
    return new Promise((resolve) => {
      if (!driverMarkerRef.current) return resolve();

      const startTime = performance.now();
      const startBearing = currentBearingRef.current;

      // Calculate bearing difference and take shortest rotation path
      let bearingDiff = bearing - startBearing;
      if (bearingDiff > 180) bearingDiff -= 360;
      if (bearingDiff < -180) bearingDiff += 360;

      const animate = (currentTime: number) => {
        // Check if animation is still valid before continuing
        if (animationToken !== routeAnimationTokenRef.current) {
          console.log('Animation cancelled - newer update arrived');
          resolve();
          return;
        }

        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1); // 0 to 1

        // Use linear interpolation for smoother, more predictable movement
        // This prevents the "ease-in-out" from causing perceived backward movement
        const easeProgress = progress;

        // Interpolate between start and end positions
        const currentLat = startLat + (endLat - startLat) * easeProgress;
        const currentLng = startLng + (endLng - startLng) * easeProgress;

        // Smoothly interpolate bearing (rotation) with slight easing
        const bearingEaseProgress = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        const currentBearing = startBearing + bearingDiff * bearingEaseProgress;
        currentBearingRef.current = currentBearing;

        // Update marker position
        if (driverMarkerRef.current) {
          driverMarkerRef.current.setLatLng([currentLat, currentLng]);
        }

        // Continue animation if not complete
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          // Update previous location after animation completes (only if still valid)
          if (animationToken === routeAnimationTokenRef.current) {
            previousDriverLocationRef.current = { latitude: endLat, longitude: endLng };
          }
          resolve();
        }
      };

      // Cancel any ongoing animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Start animation
      animationFrameRef.current = requestAnimationFrame(animate);
    });
  };

  // Car smoothing: marker always lags behind driver location, never moves ahead
  useEffect(() => {
    if (!mapState || !driverMarkerRef.current || !driverLocation) return;
    const newLat = driverLocation.latitude;
    const newLng = driverLocation.longitude;
    if (!newLat || !newLng) return;
    (async () => {
      if (previousDriverLocationRef.current) {
        let prevLat = previousDriverLocationRef.current.latitude;
        let prevLng = previousDriverLocationRef.current.longitude;
        // Only allow forward movement, filter GPS jitter
        const isForward = isForwardMovement(prevLat, prevLng, newLat, newLng);
        if (!isForward) {
          // Ignore backward jumps and jitter
          return;
        }
        // Calculate the next position: move only partway toward driver location
        const fraction = 0.7; // 70% toward the new location
        let targetLat = prevLat + (newLat - prevLat) * fraction;
        let targetLng = prevLng + (newLng - prevLng) * fraction;
        // Never allow marker to move ahead of driver location
        // If the calculated target is past the driver location, clamp to driver location
        const distToTarget = haversineDistanceMeters(prevLat, prevLng, targetLat, targetLng);
        const distToDriver = haversineDistanceMeters(prevLat, prevLng, newLat, newLng);
        if (distToTarget > distToDriver) {
          targetLat = newLat;
          targetLng = newLng;
        }
        // Animate marker to the new target position
        const myToken = ++routeAnimationTokenRef.current;
        const duration = Math.min(Math.max(distToTarget * 8, 400), 1200);
        const bearing = calculateBearing(prevLat, prevLng, targetLat, targetLng);
        await animateDriverMarker(prevLat, prevLng, targetLat, targetLng, bearing, duration, myToken);
        previousDriverLocationRef.current = { latitude: targetLat, longitude: targetLng };
      } else {
        // First update - no animation, just set position and bearing
        let initialBearing = 0;
        if (routeCoordinates.length >= 2 && routeCoordinates[0] && routeCoordinates[1]) {
          initialBearing = calculateBearing(
            routeCoordinates[0].latitude,
            routeCoordinates[0].longitude,
            routeCoordinates[1].latitude,
            routeCoordinates[1].longitude
          );
        }
        currentBearingRef.current = initialBearing;
        driverMarkerRef.current.setLatLng([newLat, newLng]);
        previousDriverLocationRef.current = { latitude: newLat, longitude: newLng };
      }
    })();
  }, [driverLocation, mapState]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  // Initialize map
  useEffect(() => {
    const mapElement = document.getElementById(mapIdRef.current);
    if (!mapElement) return;

    const map = L.map(mapIdRef.current, {
      center: [40.8054, -74.0241],
      zoom: 13,
      scrollWheelZoom: true,
      zoomControl: true,
    });

    // Add OpenStreetMap tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    // Track user interactions (zoom, pan, drag) and schedule restore after idle
    const onUserInteractionStart = () => {
      hasUserInteractedRef.current = true;
      // cancel any pending restore while user is interacting
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
        interactionTimeoutRef.current = null;
      }
    };

    const scheduleRestoreView = () => {
      // clear any existing timer
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
      // after 30s of idle, restore view to last known bounds so car is visible
      interactionTimeoutRef.current = window.setTimeout(() => {
        try {
          if (map && lastBoundsRef.current && lastBoundsRef.current.isValid && lastBoundsRef.current.isValid()) {
            map.fitBounds(lastBoundsRef.current, { padding: [50, 50], maxZoom: 16 });
          } else if (driverMarkerRef.current) {
            const ll = driverMarkerRef.current.getLatLng();
            map.setView([ll.lat, ll.lng]);
          }
        } catch (e) {
          // ignore errors
        }
        hasUserInteractedRef.current = false;
        interactionTimeoutRef.current = null;
      }, 30000);
    };

    map.on('zoomstart', onUserInteractionStart);
    map.on('movestart', onUserInteractionStart);
    map.on('zoomend', scheduleRestoreView);
    map.on('moveend', scheduleRestoreView);

    setMap(map);

    // Cleanup on component unmount
    return () => {
      // remove interaction timer
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
        interactionTimeoutRef.current = null;
      }
      // remove map event listeners
      try {
        map.off('zoomstart');
        map.off('movestart');
        map.off('zoomend');
        map.off('moveend');
      } catch (e) {
        // ignore
      }
      map.remove();
      setMap(null);
    };
  }, []);

  // Update markers and route when tripAddress or driverLocation changes
  useEffect(() => {
    if (!mapState || !tripAddress || tripAddress.length === 0) return;

    // Clear old markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Clear old polyline
    if (polylineRef.current) {
      polylineRef.current.remove();
      polylineRef.current = null;
    }

    // Only remove driver marker when driverLocation becomes null/undefined
    if (!driverLocation && driverMarkerRef.current) {
      driverMarkerRef.current.remove();
      driverMarkerRef.current = null;
      previousDriverLocationRef.current = null;
    }

    const bounds = L.latLngBounds([]);
    const validAddresses = tripAddress.filter(
      (addr) => addr?.location?.latitude != null && addr?.location?.longitude != null &&
        isValidCoordinate(addr.location.latitude, addr.location.longitude)
    );

    if (validAddresses.length === 0) return;

    // Add markers for each trip address
    validAddresses.forEach((address, index) => {
      const isPickup = index === 0;
      const isDropoff = index === validAddresses.length - 1;
      const isStop = !isPickup && !isDropoff;

      // Create custom icon HTML
      let markerColor = '#3b82f6'; // blue
      let markerLabel = `${index}`;

      if (isPickup) {
        markerColor = '#10b981'; // green
        markerLabel = 'P';
      } else if (isDropoff) {
        // markerColor = '#ef4444'; // red
        markerLabel = 'D';
      }

      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="position: relative;">
            <div style="
              width: 36px;
              height: 36px;
              background-color: ${markerColor};
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 14px;
            ">
              ${markerLabel}
            </div>
            <div style="
              position: absolute;
              top: 40px;
              left: 50%;
              transform: translateX(-50%);
              background: white;
              padding: 4px 8px;
              border-radius: 4px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              white-space: nowrap;
              font-size: 11px;
              font-weight: 500;
              max-width: 150px;
              overflow: hidden;
              text-overflow: ellipsis;
            ">
              ${isPickup ? 'Pickup' : isDropoff ? 'Drop-off' : `Stop ${index}`}
            </div>
          </div>
        `,
        iconSize: [36, 60],
        iconAnchor: [18, 18],
      });

      const marker = L.marker(
        [address.location.latitude, address.location.longitude],
        { icon: customIcon }
      ).addTo(mapState);

      marker.bindPopup(`
        <div style="max-width: 200px;">
          <strong>${address.title}</strong><br/>
          <small>${address.address}</small>
        </div>
      `);

      bounds.extend(marker.getLatLng());
      markersRef.current.push(marker);
    });
    // Add driver marker if location exists (create only if doesn't exist)
    if (driverLocation && driverLocation.latitude && driverLocation.longitude) {
      if (!driverMarkerRef.current) {
        const driverIcon = L.divIcon({
          className: 'driver-marker',
          html: `
          <div style="position: relative;">
            <!-- Pulsing ring animation -->
            <div style="
              position: absolute;
              width: 48px;
              height: 48px;
              background-color: rgba(59, 130, 246, 0.3);
              border-radius: 50%;
              animation: pulse 2s infinite;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            "></div>
            <!-- Car icon -->
            <div class="driver-car-icon" style="
              width: 36px;
              height: 36px;
              background-color: #2563eb;
              border-radius: 50%;
              border: 2px solid white;
              box-shadow: 0 4px 12px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              position: relative;
              z-index: 10;
              transform-origin: center center;
            ">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
              </svg>
            </div>
          </div>
          <style>
            @keyframes pulse {
              0% {
                transform: translate(-50%, -50%) scale(1);
                opacity: 0.7;
              }
              50% {
                transform: translate(-50%, -50%) scale(1.3);
                opacity: 0.3;
              }
              100% {
                transform: translate(-50%, -50%) scale(1);
                opacity: 0.7;
              }
            }
          </style>
        `,
          iconSize: [48, 48],
          iconAnchor: [24, 24],
        });

        driverMarkerRef.current = L.marker(
          [driverLocation.latitude, driverLocation.longitude],
          { icon: driverIcon }
        ).addTo(mapState);

        driverMarkerRef.current.bindPopup('<strong>Driver Location</strong>');
        previousDriverLocationRef.current = {
          latitude: driverLocation.latitude,
          longitude: driverLocation.longitude
        };
        bounds.extend(driverMarkerRef.current.getLatLng());
      } else {
        // If marker exists, extend bounds with current marker location
        bounds.extend(driverMarkerRef.current.getLatLng());
      }
    }

    // Draw route line if enabled
    if (showRoute && routeCoordinates.length >= 2) {
      // Use decoded polyline coordinates (like mobile app's TrackingMap)
      const polylineCoords: [number, number][] = routeCoordinates.map((coord) => [
        coord.latitude,
        coord.longitude,
      ] as [number, number]);


      polylineRef.current = L.polyline(polylineCoords, {
        color: '#3b82f6',
        weight: 4,
        opacity: 0.8,
        lineCap: 'round',
        lineJoin: 'round',
      }).addTo(mapState);

      // Add route coordinates to bounds
      polylineCoords.forEach(coord => {
        bounds.extend([coord[0], coord[1]]);
      });
      // store last bounds so we can restore view after user idle
      lastBoundsRef.current = bounds;
    } else if (showRoute && validAddresses.length >= 2) {
      // Fallback: draw straight line between markers if no route data
      const polylineCoords: [number, number][] = validAddresses.map((addr) => [
        addr.location.latitude,
        addr.location.longitude,
      ] as [number, number]);

      polylineRef.current = L.polyline(polylineCoords, {
        color: '#3b82f6',
        weight: 4,
        opacity: 0.8,
        lineCap: 'round',
        lineJoin: 'round',
        dashArray: '10, 10', // Dashed line to indicate it's not actual route
      }).addTo(mapState);
      // store last bounds so we can restore view after user idle
      lastBoundsRef.current = bounds;
    }

    // update lastBoundsRef even if no polyline (so marker bounds kept)
    lastBoundsRef.current = lastBoundsRef.current || bounds;

    // Fit map to bounds only on initial load or if user hasn't interacted
    if (bounds.isValid() && !hasUserInteractedRef.current && isInitialFitRef.current) {
      mapState.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 16,
      });
      isInitialFitRef.current = false;
    }
  }, [tripAddress, driverLocation, mapState, showRoute, routeCoordinates]);

  const handleRecenter = () => {
    if (!mapState) return;

    try {
      if (lastBoundsRef.current && lastBoundsRef.current.isValid && lastBoundsRef.current.isValid()) {
        mapState.fitBounds(lastBoundsRef.current, {
          padding: [50, 50],
          maxZoom: 16,
        });
      } else if (driverMarkerRef.current) {
        const ll = driverMarkerRef.current.getLatLng();
        mapState.setView([ll.lat, ll.lng], 16);
      }
    } catch (e) {
      // ignore
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <div id={mapIdRef.current} style={{ height: '100%', width: '100%', zIndex: 0 }} />
      {routeDurationText && (
        <div style={{
          position: 'absolute',
          top: 8,
          right: 8,
          background: 'rgba(255,255,255,0.95)',
          borderRadius: 6,
          padding: '6px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          fontSize: 14,
          fontWeight: 500,
          zIndex: 10
        }}>
          Route Time: {routeDurationText}
        </div>
      )}
      <button
        type="button"
        onClick={handleRecenter}
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          zIndex: 20,
          background: 'white',
          borderRadius: 9999,
          padding: '8px 14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        Recenter
      </button>
    </div>
  );
}

